/* Spin & Win — custom element <spin-and-win>
   Wired to ZOP spin-wheel endpoints. Segment count is driven by the API. */

(function () {
  if (customElements.get('spin-and-win')) return;

  const API_CONFIG = {
    baseUrl: window.FoxTheme?.zop?.baseUrl,
    bearerToken: window.FoxTheme?.zop?.authToken,
    endpoints: {
      showWheel: '/v1/zop/tracker/show-zop-spin-wheel',
      spinResult: '/v1/zop/tracker/spin-the-wheel-result',
    },
  };

  const STORAGE_KEY = 'spin_and_win:played';
  const SPIN_DURATION_MS = 3500;
  const TOAST_DURATION_MS = 1000;
  const REDEEM_REDIRECT_DELAY_MS = 500;
  const BASE_FULL_SPINS = 8;
  const FALLBACK_COLORS = ['#FDE68A', '#E5E7EB', '#C7D2FE', '#99F6E4', '#E9D5FF', '#FECDD3'];
  const CONFETTI_PALETTE = ['#004ebb', '#465e8b', '#FDE68A', '#99F6E4', '#E9D5FF', '#FECDD3', '#10B981', '#F59E0B', '#ba1a1a'];

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // --- API calls --------------------------------------------------------------

  function authHeaders() {
    return { Authorization: 'Bearer ' + API_CONFIG.bearerToken };
  }

  async function fetchSegments() {
    const res = await fetch(API_CONFIG.baseUrl + API_CONFIG.endpoints.showWheel, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error('Failed to load segments');
    const data = await res.json();
    const coupons = data && data.response && data.response.coupons;
    if (!Array.isArray(coupons) || coupons.length === 0) {
      throw new Error('No coupons returned');
    }
    return coupons;
  }

  async function fetchSpinResult() {
    const res = await fetch(API_CONFIG.baseUrl + API_CONFIG.endpoints.spinResult, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error('Spin failed');
    const data = await res.json();
    const coupon = data && data.response;
    if (!coupon || !coupon._id) throw new Error('Invalid spin result');
    return coupon;
  }

  // --- Format helpers ---------------------------------------------------------

  function formatPriceLabel(seg) {
    return seg.coupon_display || '';
  }

  function formatPriceValue(seg) {
    if (!seg.selling_price || seg.selling_price <= 0) return '';
    return `₹${seg.selling_price}`;
  }

  function formatWinTitle(seg) {
    return seg.product_title || seg.coupon_display || '';
  }

  function isOfferSegment(seg) {
    return !seg.selling_price || seg.selling_price <= 0;
  }

  // Split a coupon_display string into 3 lines for the offer treatment:
  // top eyebrow / big hero / bottom badge. Deterministic — empty slots collapse.
  function splitOfferText(text) {
    const tokens = String(text || '').trim().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return { top: '', hero: '', bottom: '' };
    if (tokens.length === 1) return { top: '', hero: tokens[0], bottom: '' };
    if (tokens.length === 2) return { top: '', hero: tokens[0], bottom: tokens[1] };
    if (tokens.length === 3) return { top: tokens[0], hero: tokens[1], bottom: tokens[2] };
    return { top: tokens[0], hero: tokens.slice(1, -1).join(' '), bottom: tokens[tokens.length - 1] };
  }

  function appendOfferLines(parent, parts, classes) {
    if (parts.top) {
      const top = document.createElement('span');
      top.className = classes.top;
      top.textContent = parts.top;
      parent.appendChild(top);
    }
    if (parts.hero) {
      const hero = document.createElement('span');
      hero.className = classes.hero;
      hero.textContent = parts.hero;
      parent.appendChild(hero);
    }
    if (parts.bottom) {
      const bottom = document.createElement('span');
      bottom.className = classes.bottom;
      bottom.textContent = parts.bottom;
      parent.appendChild(bottom);
    }
  }

  // Shopify CDN supports a `width` query param for on-the-fly resizing.
  // The modal image spans the card width (max 384 CSS px), so request 2x for retina.
  function withCdnWidth(url, width) {
    if (!url) return url;
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (err) {
      return url;
    }
  }

  const PRIZE_IMAGE_WIDTH = 400;

  // --- Analytics --------------------------------------------------------------

  function extractBrandHandle(url) {
    if (!url) return '';
    try {
      const u = new URL(url, window.location.origin);
      const parts = u.pathname.split('/').filter(Boolean);
      return parts[parts.length - 1] || '';
    } catch (err) {
      return '';
    }
  }

  function getOutcomeType(seg) {
    return isOfferSegment(seg) ? 'offer' : 'product';
  }

  function trackSpinEvent(name, meta) {
    if (typeof window.sendAnalytics !== 'function') return;
    const payload = { user_id: window.gfp_customer?.id, ...meta };
    window.sendAnalytics(name, 'spin_and_win', payload);
  }

  // --- Custom element ---------------------------------------------------------

  class SpinAndWin extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._currentRotation = 0;
      this._isSpinning = false;
      this._segments = [];
      this._segmentAngle = 0;
      this._engaged = false;

      this._cacheRefs();
      this._bindEvents();
      this._initConfetti();
      this._init();
    }

    disconnectedCallback() {
      this._confetti?.destroy();
    }

    _initConfetti() {
      if (!this.refs.confetti || typeof window.Confetti !== 'function') return;
      this._confetti = new window.Confetti(this.refs.confetti, { palette: CONFETTI_PALETTE });
    }

    _cacheRefs() {
      this.refs = {
        loading: this.querySelector('[data-state="loading"]'),
        game: this.querySelector('[data-state="game"]'),
        error: this.querySelector('[data-state="error"]'),
        played: this.querySelector('[data-state="played"]'),
        wheel: this.querySelector('[data-wheel]'),
        pointer: this.querySelector('.saw__pointer'),
        spinBtn: this.querySelector('[data-spin]'),
        retryBtn: this.querySelector('[data-retry]'),
        playedCouponCode: this.querySelector('[data-played-coupon-code]'),
        playedCopyBtn: this.querySelector('[data-played-copy]'),
        playedRedeemBtn: this.querySelector('[data-played-redeem]'),
        modal: this.querySelector('[data-modal]'),
        modalCard: this.querySelector('[data-modal-card]'),
        modalClose: this.querySelector('[data-modal-close]'),
        modalImage: this.querySelector('[data-prize-image]'),
        modalImagePlaceholder: this.querySelector('[data-prize-image-placeholder]'),
        winTitle: this.querySelector('[data-prize-title]'),
        winSubtitle: this.querySelector('[data-prize-subtitle]'),
        couponCode: this.querySelector('[data-coupon-code]'),
        copyBtn: this.querySelector('[data-copy]'),
        redeemBtn: this.querySelector('[data-redeem]'),
        toast: this.querySelector('[data-toast]'),
        confetti: this.querySelector('[data-confetti]'),
      };
      this._toastMessage = this.dataset.toastMessage || 'Coupon copied!';
    }

    _bindEvents() {
      this.refs.spinBtn?.addEventListener('click', () => this._handleSpin());
      this.refs.retryBtn?.addEventListener('click', () => this._init());
      this.refs.modalClose?.addEventListener('click', () => this._closeModal());
      this.refs.copyBtn?.addEventListener('click', () => this._copyCoupon());
      this.refs.redeemBtn?.addEventListener('click', () => this._redeem());
      this.refs.playedCopyBtn?.addEventListener('click', () => this._copyCoupon());
      this.refs.playedRedeemBtn?.addEventListener('click', () => this._redeem());
    }

    _setState(state) {
      ['loading', 'game', 'error', 'played'].forEach((name) => {
        if (this.refs[name]) this.refs[name].hidden = name !== state;
      });
    }

    async _init() {
      const played = this._readSession();
      if (played) {
        this._showPlayed(played);
        return;
      }

      this._setState('loading');
      try {
        const segments = await fetchSegments();
        this._segments = segments;
        this._segmentAngle = 360 / segments.length;
        this._renderWheel();
        this._setState('game');
        trackSpinEvent('spin_widget_impression', { referrer_url: document.referrer });
      } catch (err) {
        console.error('[spin-and-win] Failed to load segments:', err);
        trackSpinEvent('spin_widget_load_failed', { referrer_url: document.referrer });
        this._setState('error');
      }
    }

    _renderWheel() {
      const wheel = this.refs.wheel;
      if (!wheel) return;
      wheel.innerHTML = '';
      this._segments.forEach((seg, idx) => {
        const el = document.createElement('div');
        el.className = 'saw__segment';
        el.style.transform = `rotate(${idx * this._segmentAngle}deg)`;
        el.style.backgroundColor = seg.color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];

        const content = document.createElement('div');
        content.className = 'saw__segment-content';

        if (isOfferSegment(seg)) {
          el.classList.add('saw__segment--offer');
          appendOfferLines(content, splitOfferText(seg.coupon_display), {
            top: 'saw__offer-top',
            hero: 'saw__offer-hero',
            bottom: 'saw__offer-bottom',
          });
        } else {
          const label = document.createElement('span');
          label.className = 'saw__segment-label';
          label.textContent = formatPriceLabel(seg);
          content.appendChild(label);

          const priceText = formatPriceValue(seg);
          if (priceText) {
            const at = document.createElement('span');
            at.className = 'saw__segment-at';
            at.textContent = 'at';
            content.appendChild(at);

            const price = document.createElement('span');
            price.className = 'saw__segment-price';
            price.textContent = priceText;
            content.appendChild(price);
          }
        }

        el.appendChild(content);
        wheel.appendChild(el);
      });
    }

    async _handleSpin() {
      if (this._isSpinning || this._segments.length === 0) return;
      this._isSpinning = true;
      this.refs.spinBtn.disabled = true;
      const originalLabel = this.refs.spinBtn.textContent;
      this.refs.spinBtn.textContent = 'Spinning…';
      trackSpinEvent('spin_now_cta_clicked', { referrer_url: document.referrer });

      let winning;
      try {
        winning = await fetchSpinResult();
      } catch (err) {
        console.error('[spin-and-win] Spin request failed:', err);
        trackSpinEvent('spin_api_failed', {});
        this._isSpinning = false;
        this.refs.spinBtn.disabled = false;
        this.refs.spinBtn.textContent = originalLabel;
        return;
      }

      trackSpinEvent('spin_coupon_assigned', {
        spin_id: winning._id,
        coupon_code: winning.coupon_code,
        outcome_type: getOutcomeType(winning),
        device_type: typeof window.detectDeviceType === 'function' ? window.detectDeviceType() : '',
      });

      const targetIndex = this._segments.findIndex((s) => s._id === winning._id);
      if (targetIndex === -1) {
        // Result coupon isn't in the displayed list — treat as data error.
        console.error('[spin-and-win] Result coupon not in displayed segments:', winning._id);
        this._isSpinning = false;
        this.refs.spinBtn.disabled = false;
        this.refs.spinBtn.textContent = originalLabel;
        this._setState('error');
        return;
      }

      this.refs.pointer?.classList.add('is-visible');

      // Warm the prize image cache while the wheel spins so it's ready when the
      // modal opens — otherwise it pops in noticeably after the coupon.
      if (winning.image_url) {
        const preload = new Image();
        preload.src = withCdnWidth(winning.image_url, PRIZE_IMAGE_WIDTH);
      }

      // Drive the wheel to the target segment. Wheel rotates CW; segment 0 sits
      // at top, so we subtract the target's angle to bring it under the pointer.
      const targetRotation = BASE_FULL_SPINS * 360 - targetIndex * this._segmentAngle;
      this._currentRotation += targetRotation;
      this.refs.wheel.style.transform = `rotate(${this._currentRotation}deg)`;

      await wait(SPIN_DURATION_MS);

      this._isSpinning = false;
      this.refs.spinBtn.disabled = false;
      this.refs.spinBtn.textContent = originalLabel;

      this._writeSession(winning);
      this._showWinModal(winning);
    }

    _showWinModal(prize) {
      this._engaged = false;
      trackSpinEvent('spin_result_viewed', {
        spin_id: prize._id,
        outcome_type: getOutcomeType(prize),
        coupon_code: prize.coupon_code,
      });
      const winTitle = this.refs.winTitle;
      const winSubtitle = this.refs.winSubtitle;
      winSubtitle.innerHTML = '';
      if (isOfferSegment(prize)) {
        winSubtitle.classList.add('saw__win-subtitle--offer');
        appendOfferLines(winSubtitle, splitOfferText(prize.coupon_display), {
          top: 'saw__win-offer-top',
          hero: 'saw__win-offer-hero',
          bottom: 'saw__win-offer-bottom',
        });
        winTitle.textContent = prize.selling_price === 0 ? '' : (prize.product_title || prize.coupon_title || '');
      } else {
        winSubtitle.classList.remove('saw__win-subtitle--offer');
        winSubtitle.textContent = formatWinTitle(prize);
        winTitle.textContent = prize.selling_price === 0 ? '' : (prize.coupon_title || '');
      }
      this.refs.couponCode.textContent = prize.coupon_code;
      const sizedImageUrl = prize.image_url ? withCdnWidth(prize.image_url, PRIZE_IMAGE_WIDTH) : '';
      if (this.refs.modalImage && sizedImageUrl) {
        this.refs.modalImage.src = sizedImageUrl;
        this.refs.modalImage.alt = prize.product_title || '';
      }
      if (this.refs.modalImagePlaceholder && sizedImageUrl) {
        this.refs.modalImagePlaceholder.style.backgroundImage = `url("${sizedImageUrl}")`;
      }
      this._currentPrize = prize;

      this.refs.modal.hidden = false;
      requestAnimationFrame(() => {
        this.refs.modalCard.classList.add('is-visible', 'is-pop');
        this.refs.modal.classList.add('is-shake');
      });

      this._confetti?.burst();

      setTimeout(() => {
        this.refs.modal.classList.remove('is-shake');
        this.refs.modalCard.classList.remove('is-pop');
      }, 600);
    }

    _closeModal() {
      if (!this._engaged && this._currentPrize) {
        trackSpinEvent('spin_result_dismissed', {
          spin_id: this._currentPrize._id,
          outcome_type: getOutcomeType(this._currentPrize),
        });
      }
      this.refs.modalCard.classList.remove('is-visible');
      this._confetti?.stop();
      const wonPrize = this._currentPrize;
      setTimeout(() => {
        this.refs.modal.hidden = true;
        // After a win, the session lock is already set. Reflect that in the UI
        // so the spin button can't be pressed again in this session.
        if (wonPrize) this._showPlayed(wonPrize);
      }, 300);
    }

    async _copyCoupon() {
      if (!this._currentPrize) return;
      this._engaged = true;
      trackSpinEvent('coupon_code_copied', {
        spin_id: this._currentPrize._id,
        coupon_code: this._currentPrize.coupon_code,
        brand_name: extractBrandHandle(this._currentPrize.redirect_url),
        discount_value: this._currentPrize.coupon_display,
      });
      await this._writeClipboard(this._currentPrize.coupon_code);
      this._showToast(this._toastMessage);
    }

    async _redeem() {
      if (!this._currentPrize) return;
      this._engaged = true;
      const code = this._currentPrize.coupon_code;
      const url = this._currentPrize.redirect_url;
      trackSpinEvent('redeem_now_cta_clicked', {
        spin_id: this._currentPrize._id,
        coupon_code: code,
        destination_url: url,
      });
      await this._writeClipboard(code);
      this._showToast(this._toastMessage);
      setTimeout(() => {
        if (url) window.location.assign(url);
      }, REDEEM_REDIRECT_DELAY_MS);
    }

    async _writeClipboard(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return;
        }
      } catch (err) {
        // fall through to legacy path
      }
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (err) {
        console.warn('[spin-and-win] Clipboard copy failed:', err);
      }
    }

    _showToast(message) {
      const toast = this.refs.toast;
      if (!toast) return;
      toast.textContent = message;
      toast.hidden = false;
      requestAnimationFrame(() => toast.classList.add('is-visible'));
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => { toast.hidden = true; }, 200);
      }, TOAST_DURATION_MS);
    }

    // --- Session lock ---------------------------------------------------------

    _readSession() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (err) {
        return null;
      }
    }

    _writeSession(prize) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          _id: prize._id,
          coupon_code: prize.coupon_code,
          coupon_display: prize.coupon_display,
          product_title: prize.product_title,
          coupon_title: prize.coupon_title,
          image_url: prize.image_url,
          redirect_url: prize.redirect_url,
          selling_price: prize.selling_price,
        }));
      } catch (err) {
        // sessionStorage unavailable (e.g., private mode) — silently allow re-spin
      }
    }

    _showPlayed(played) {
      this._currentPrize = played;
      if (this.refs.playedCouponCode) {
        this.refs.playedCouponCode.textContent = played.coupon_code;
      }
      this._setState('played');
    }

  }

  customElements.define('spin-and-win', SpinAndWin);
})();
